/**
 * ENG-436 — dispatch a routed query to the interface that should service it.
 *
 * `use-query-router` decides *what* should happen; this decides *where* the
 * user lands. Kept separate so the decision logic stays testable without a
 * router or a Pinia store.
 */

import type { QueryMode, QueryRoute, ResolveQueryRouteOptions } from './use-query-router';
import type { ChatSurface } from '@/composables/analytics/analytics-service';
import type { DocumentFilter } from '@/types/measure-document-types';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAnalytics } from '@/composables/analytics/analytics-service';
import { getCurrentRouteName } from '@/composables/analytics/current-route';
import { classifyQuery } from '@/services/llm-service';
import { useChatSessionStore } from '@/stores/chat-session-store.ts';
import { useLumaDockStore } from '@/stores/luma-dock-store.ts';
import { shouldExplainRoute } from '@/utils/query-routing';
import { resolveQueryRoute } from './use-query-router';

/* ---------------------------------------------------------------------------------------------- */

/**
 * Query param carrying the input a search was rewritten from. Its presence is
 * what lets the results screen account for a search box holding words the user
 * didn't type, and offer Luma the question in their own wording instead.
 */
export const ROUTED_FROM_PARAM = 'ask';

/** Where a hand-off to Luma lands. */
export type ChatTarget = 'route' | 'dock';

export interface QueryNavigationOptions {
   /**
    * Where a chat hand-off lands: the full-screen `/chats/:sessionId` view
    * (`'route'`, the default) or the global Luma drawer (`'dock'`).
    *
    * The search page asks in the drawer, so a question typed into the search box
    * keeps the results it was asked about on screen, and Luma looks there the
    * way it looks everywhere else.
    */
   chatTarget?: ChatTarget;

   /**
    * Which input the question came from, reported on `chat:ask`.
    *
    * Taken once here rather than per call, because a component always knows its
    * own surface and a per-call argument is a thing to forget. It travels with
    * the question to whoever ends up recording it.
    */
   surface?: ChatSurface;
}

export interface GoToSearchOptions {
   /**
    * Original input, when the search being run is a rewrite of it. Surfaces the
    * "switch to Luma" affordance on the results screen. Leave unset for a search
    * that speaks for itself — `shouldExplainRoute` draws that line.
    */
   routedFrom?: string | null;
   /** Facet filters to arrive pre-applied. */
   filters?: DocumentFilter[];
}

/**
 * Encode filters the way the search page reads them back.
 *
 * Must stay in step with `search-page.vue`'s `parseJsonParam` — it expects a
 * URI-encoded JSON payload, not a bare one.
 */
function encodeFilters(filters: DocumentFilter[]): string {
   return encodeURIComponent(JSON.stringify(filters));
}

/* ---------------------------------------------------------------------------------------------- */

export function useQueryNavigation({
   chatTarget = 'route',
   surface = 'chat_page',
}: QueryNavigationOptions = {}) {
   const router = useRouter();
   const chatSessionStore = useChatSessionStore();
   const lumaDock = useLumaDockStore();
   const { trackEvent } = useAnalytics();

   /** True while a query is being analysed, a session created, or both. */
   const isRouting = ref(false);
   /** Set when the handoff failed and the caller should surface an error. */
   const routingError = ref(false);

   /* ------------------------------------------------------------------------------------------- */

   async function goToSearch(query: string, options: GoToSearchOptions = {}) {
      const trimmed = query.trim();
      const filters = options.filters ?? [];
      // Filters alone are a complete search; the results screen shows results
      // whenever either half is present.
      if (!trimmed && filters.length === 0)
         return;

      trackEvent('search:execute', { q: trimmed, filters });
      await router.push({
         name: 'search',
         query: {
            ...(trimmed ? { q: trimmed } : {}),
            ...(filters.length ? { f: encodeFilters(filters) } : {}),
            ...(options.routedFrom ? { [ROUTED_FROM_PARAM]: options.routedFrom } : {}),
         },
      });
   }

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Open a fresh Luma thread on `message`.
    *
    * With `chatTarget: 'dock'` the drawer takes the question and owns the thread
    * from there — it creates the session, sends, and reports its own failures —
    * so this returns as soon as the hand-off is made and the user watches the
    * answer arrive without leaving the page.
    *
    * Otherwise it navigates to the full-screen thread as soon as the session
    * exists, so the user sees their message land rather than staring at a
    * spinner on the previous screen; the answer streams in afterwards.
    */
   async function startChat(message: string): Promise<boolean> {
      const trimmed = message.trim();
      if (!trimmed)
         return false;

      // Neither branch records the ask. Whoever sends the message does, so one
      // message is one event no matter which route it took to get there. This
      // used to emit as well, which counted every opening question twice and
      // split single threads across the two URLs the two emits caught.
      if (chatTarget === 'dock') {
         lumaDock.askLuma(trimmed, surface);
         return true;
      }

      // Read before navigating. This branch hands the question to the
      // full-screen thread, so by the time the ask is reported the router says
      // `chat` and the screen the question was actually typed on is gone. Left
      // to the router, a new conversation started from the chat home would be
      // indistinguishable from a follow-up inside an existing one.
      const askedFrom = getCurrentRouteName();

      const session = await chatSessionStore.createSession();
      if (!session)
         return false;

      // `new=true` tells the chat view this thread's messages don't need fetching.
      await router.replace({
         name: 'chat',
         params: { sessionId: session.id },
         query: { new: 'true' },
      });

      const response = await chatSessionStore.sendMessage(trimmed, session.id, { surface, screen: askedFrom });
      if (response?.thread) {
         chatSessionStore.updateSessionTitleOnFirstMessage(response.thread);
      }

      return true;
   }

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Analyse `input` and hand off to whichever interface fits.
    *
    * Returns the route that was taken so callers can report it, or `null` when
    * the input was empty or the handoff failed (`routingError` is set).
    */
   async function routeQuery(
      input: string,
      options: ResolveQueryRouteOptions = {},
   ): Promise<QueryRoute | null> {
      const trimmed = input.trim();
      if (!trimmed)
         return null;

      isRouting.value = true;
      routingError.value = false;

      try {
         const route = await resolveQueryRoute(trimmed, options);

         if (route.mode === 'search') {
            // Only explain the landing when the words in the search box aren't
            // the ones they typed — see `shouldExplainRoute`.
            await goToSearch(route.searchQuery, {
               routedFrom: shouldExplainRoute(route) ? trimmed : null,
            });
            return route;
         }

         const started = await startChat(route.message);
         if (!started) {
            routingError.value = true;
            return null;
         }
         return route;
      }
      catch (error) {
         console.error('Failed to route query:', error);
         routingError.value = true;
         return null;
      }
      finally {
         isRouting.value = false;
      }
   }

   /* ------------------------------------------------------------------------------------------- */

   /** Force a specific interface, skipping the analysis round. */
   function routeQueryAs(input: string, mode: QueryMode) {
      return routeQuery(input, { force: mode });
   }

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Leave a Luma thread for the search results screen.
    *
    * A conversational message rarely makes a good keyword query, so it gets
    * rewritten first — the same refinement the analyser applies on the way in.
    * If the rewrite is unavailable the raw message still searches fine.
    */
   async function goToSearchFromChat(
      message: string,
      classify: (question: string) => Promise<{ search_query: string } | null> = classifyQuery,
   ): Promise<boolean> {
      const trimmed = message.trim();
      if (!trimmed)
         return false;

      isRouting.value = true;
      routingError.value = false;
      try {
         const classification = await classify(trimmed);
         await goToSearch(classification?.search_query || trimmed, { routedFrom: null });
         return true;
      }
      catch (error) {
         console.error('Failed to switch to search:', error);
         // Same contract as `routeQuery`: the caller renders `routingError`. A
         // failed switch used to end with the button simply stopping, which
         // reads as "nothing here" rather than "that didn't work".
         routingError.value = true;
         return false;
      }
      finally {
         isRouting.value = false;
      }
   }

   return {
      isRouting,
      routingError,
      goToSearch,
      goToSearchFromChat,
      startChat,
      routeQuery,
      routeQueryAs,
   };
}
